import React, { useEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import Select from "react-select";
import { Button, Badge, Tooltip, OverlayTrigger } from "react-bootstrap";
import Drawer from '@mui/material/Drawer';
import DatePicker from "react-multi-date-picker";
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { Row, Col, Tab, Nav } from "react-bootstrap";
import moment from 'moment';
import axios from 'axios';
import itemDefault from '../../../../images/items/default_item.png';
import staffDefault from '../../../../images/staff/member_1.png';

const Manage_FoodWastage = () => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const onInit = () => {};

    // DEFAULT → TODAY shown in UI
    const [dateFilter, setDateFilter] = useState("today");

    const [todayDate, setTodayDate] = useState(new Date());
    const [weekStartDate, setWeekStartDate] = useState(moment().startOf('isoWeek').toDate());
    const [weekEndDate, setWeekEndDate] = useState(moment().endOf('isoWeek').toDate());
    const [monthDate, setMonthDate] = useState(new Date());
    const [customStartDate, setCustomStartDate] = useState(new Date());
    const [customEndDate, setCustomEndDate] = useState(new Date());

    const [filterOpen, setFilterOpen] = useState(false);
    const [key, setKey] = useState('food_waste');

    const [wastageList, setWastageList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const dateFilterOptions = [
        { value: "all", label: "All" },
        { value: "today", label: "Today" },
        { value: "this_week", label: "This week" },
        { value: "this_month", label: "This Month" },
        { value: "custom", label: "Custom Date" }
    ];

    const formatQty = (value) => {
        if (!value && value !== 0) return "0";
        let num = parseFloat(value);
        if (isNaN(num)) return value;
        if (num % 1 === 0) return num.toString();
        return num.toString().replace(/0+$/, "");
    };

    // Normalize date returned by react-multi-date-picker (DateObject) to native Date
    const normalizeDate = (d) => {
        if (!d) return null;
        // react-multi-date-picker DateObject has toDate()
        if (typeof d.toDate === "function") return d.toDate();
        // already a JS Date
        if (d instanceof Date) return d;
        // else try parseable string
        const parsed = new Date(d);
        if (!isNaN(parsed)) return parsed;
        return null;
    };

    /**
     * fetchWastage(applyFilter = true, overridePage = null)
     * - applyFilter: whether to apply date filters (true by default)
     * - overridePage: if you want to force page (eg. 1 after Apply/Reset)
     */
    const fetchWastage = (applyFilter = true, overridePage = null) => {
        const pageParam = overridePage ?? page;
        const params = {
            page: pageParam,
            per_page: perPage,
            search: searchTerm || undefined
        };

        if (applyFilter) {
            switch (dateFilter) {
                case "today":
                    params.start_date = moment(todayDate).format("YYYY-MM-DD");
                    params.end_date = moment(todayDate).format("YYYY-MM-DD");
                    break;

                case "this_week":
                    params.start_date = moment(weekStartDate).format("YYYY-MM-DD");
                    params.end_date = moment(weekEndDate).format("YYYY-MM-DD");
                    break;

                case "this_month":
                    params.month = moment(monthDate).month() + 1;
                    params.year = moment(monthDate).year();
                    break;

                case "custom":
                    params.start_date = moment(customStartDate).format("YYYY-MM-DD");
                    params.end_date = moment(customEndDate).format("YYYY-MM-DD");
                    break;

                case "all":
                default:
                    // no date params
                    break;
            }
        }

        axios.get(`${baseURL}/wastage-list`, { params })
            .then(res => {
                const { data, total_count } = res.data || {};
                setWastageList(data || []);
                setTotalCount(total_count || 0);
                setTotalPages(Math.max(1, Math.ceil((total_count || 0) / perPage)));
            })
            .catch(err => {
                console.error("fetchWastage error:", err);
            });
    };

    // Reset filter -> back to TODAY and fetch page 1
    const handleResetFilter = () => {
        setDateFilter("today");
        setTodayDate(new Date());
        setWeekStartDate(moment().startOf('isoWeek').toDate());
        setWeekEndDate(moment().endOf('isoWeek').toDate());
        setMonthDate(new Date());
        setCustomStartDate(new Date());
        setCustomEndDate(new Date());
        setSearchTerm("");
        setPage(1);
        fetchWastage(true, 1);
    };

    // Apply filter -> fetch page 1 with current filter selections
    const handleApplyFilter = () => {
        setPage(1);
        fetchWastage(true, 1);
        setFilterOpen(false);
    };

    // When the user selects a different filter option, update defaults for that filter
    const handleDateFilterChange = (option) => {
        const val = option?.value || "all";
        setDateFilter(val);

        if (val === "today") {
            setTodayDate(new Date());
        } else if (val === "this_week") {
            setWeekStartDate(moment().startOf('isoWeek').toDate());
            setWeekEndDate(moment().endOf('isoWeek').toDate());
        } else if (val === "this_month") {
            setMonthDate(new Date());
        }
        // for custom and all, keep existing values (user can pick)
        // we don't auto-fetch here — user must click Apply (keeps UX consistent)
    };

    // Fetch initially and whenever page/perPage/searchTerm changes.
    // We use applyFilter = true so the default TODAY filter will apply on mount.
    useEffect(() => {
        fetchWastage(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, searchTerm]);

    return (
        <>
            <div className="d-flex align-items-center justify-content-between page-titles px-0 py-1 mx-0">
                <h4 className='breadcrumb-header'>Manage Food Wastage</h4>
                <div className='d-flex gap-2'>
                    <label className='fs-5 text-danger font-w600'>
                        {moment().format("DD-MMM-YYYY")}
                    </label>
                    <Button variant="primary" size="sm" onClick={() => setFilterOpen(true)}>
                        <i className="fa-solid fa-filter me-2"></i> Filter
                    </Button>
                </div>
            </div>

            {/* MAIN TABLE */}
            <Col xl={12} className='mb-3'>
                <div className="custom-tab-1">
                    <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>
                        <Nav as="ul" className="nav-tabs">
                            <Nav.Item as="li">
                                <Nav.Link eventKey="raw_material">
                                    <Link to="/manage-wastage" className="d-flex align-items-center text-decoration-none">
                                        <i className="fa-solid fa-sack-xmark me-2"></i>
                                        <label className="fs-6">Raw Material Wastage</label>
                                        {/* <Badge bg=" badge-circle" className='badge-outline-danger ms-2 fw-bold fs-8'>
                                            {totalCount}
                                        </Badge> */}
                                    </Link>
                                </Nav.Link>
                            </Nav.Item>

                            <Nav.Item as="li">
                                <Nav.Link eventKey="food_waste">
                                    <Link to="/manage-food-wastage" className="d-flex align-items-center text-decoration-none">
                                        <i className="fa-solid fa-drumstick-bite me-2"></i>
                                        <label className="fs-6">Food Wastage</label>
                                        {/* <Badge bg=" badge-circle" className='badge-outline-danger ms-2 fw-bold fs-8'>
                                            {totalCount}
                                        </Badge> */}
                                    </Link>
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content className="pt-4">
                            <Tab.Pane eventKey="food_waste">
                                <Row>
                                    <Col xl={12}>
                                        <div className="table-container">

                                            {/* Show + Search */}
                                            <div className="d-flex justify-content-between mb-3">
                                                <div>
                                                    <label className="me-2">Show</label>
                                                    <select
                                                        className="form-select d-inline w-auto"
                                                        value={perPage}
                                                        onChange={(e) => {
                                                            setPerPage(Number(e.target.value));
                                                            setPage(1);
                                                        }}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder="Search item / staff..."
                                                        className="form-control"
                                                        value={searchTerm}
                                                        onChange={(e) => {
                                                            setSearchTerm(e.target.value);
                                                            setPage(1);
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <table className="item_data_table table align-middle table-row-dashed table-striped table-hover fs-7 gy-1 gs-2 dataTable">
                                                <thead>
                                                    <tr className=" text-start text-muted fw-bold fs-6 gs-0 th_head">
                                                        <th className='min-w-50px text-start'>Date</th>
                                                        <th className='min-w-100px text-start'>Items</th>
                                                        <th className='min-w-50px'>Cooked/Wasted Qty</th>
                                                        <th className='min-w-80px'>Avg / Wasted Cost</th>
                                                        <th className='min-w-100px text-start'>Staff</th>
                                                        <th className='min-w-100px text-start'>Reason</th>
                                                    </tr>
                                                </thead>

                                                <tbody className="text-gray-600 fw-semibold fs-7">
                                                    {wastageList.length === 0 && (
                                                        <tr>
                                                            <td colSpan="6" className="text-center text-danger">
                                                                No records found
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {wastageList.map((row, index) => (
                                                        <tr key={index}>
                                                            <td className='text-black'>
                                                                {moment(row.date).format("DD-MMM-YYYY")}
                                                            </td>

                                                            <td>
                                                                <div className='d-flex align-items-center gap-2'>
                                                                    <LightGallery
                                                                        onInit={onInit}
                                                                        speed={500}
                                                                        plugins={[lgThumbnail, lgZoom]}
                                                                    >
                                                                        <div data-src={row.item?.image || itemDefault}>
                                                                            <img
                                                                                src={row.item?.image || itemDefault}
                                                                                alt=""
                                                                                className='cursor-pointer w-40px h-40px'
                                                                            />
                                                                        </div>
                                                                    </LightGallery>

                                                                    <label className='text-black'>{row.item?.name}</label>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <span className='text-primary fw-bold border px-2 py-1 rounded'>
                                                                    {formatQty(row.total_used)} {row.unit}
                                                                </span>

                                                                &nbsp;|&nbsp;

                                                                <span className='text-danger fw-bold border px-2 py-1 rounded'>
                                                                    {formatQty(row.total_wasted)} {row.unit}
                                                                </span>
                                                            </td>

                                                            <td> - </td>

                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <img
                                                                        src={row.staff?.image || staffDefault}
                                                                        className='cursor-pointer w-40px h-40px'
                                                                    />
                                                                    <span className='text-black'>{row.staff?.name}</span>
                                                                </div>
                                                            </td>

                                                            <td className="text-black">
                                                                <div style={{ maxWidth: "180px", maxHeight: "50px", overflowY: "auto" }}>
                                                                    {row.reason}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {/* Pagination */}
                                            <div className="d-flex justify-content-between mt-3">
                                                <span>
                                                    Showing {(page - 1) * perPage + 1} to {(page - 1) * perPage + wastageList.length} of {totalCount}
                                                </span>

                                                <ul className="pagination mb-0">
                                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => {
                                                                if (page > 1) setPage(page - 1);
                                                            }}
                                                        >
                                                            Prev
                                                        </button>
                                                    </li>

                                                    {Array.from({ length: totalPages }).map((_, i) => (
                                                        <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => setPage(i + 1)}>
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}

                                                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => {
                                                                if (page < totalPages) setPage(page + 1);
                                                            }}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>

                                        </div>
                                    </Col>
                                </Row>
                            </Tab.Pane>
                        </Tab.Content>

                    </Tab.Container>
                </div>
            </Col>

            {/* FILTER DRAWER */}
           <Drawer
                anchor="right"
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                sx={{ '& .MuiDrawer-paper': { width: 350, padding: '1rem' } }}
            >
                <h5 className='text-danger mb-3'>Filter Wastage</h5>

                <Select
                    value={dateFilterOptions.find(option => option.value === dateFilter)}
                    onChange={handleDateFilterChange}
                    options={dateFilterOptions}
                />

                {dateFilter === "today" && (
                    <DatePicker
                        value={todayDate}
                        onChange={(d) => {
                            const nd = normalizeDate(d);
                            if (nd) setTodayDate(nd);
                        }}
                        format="DD-MM-YYYY"
                    />
                )}

                {dateFilter === "this_week" && (
                    <div className='d-flex gap-2'>
                        <DatePicker
                            value={weekStartDate}
                            onChange={(d) => {
                                const nd = normalizeDate(d);
                                if (nd) setWeekStartDate(nd);
                            }}
                            format="DD-MM-YYYY"
                        />
                        <DatePicker
                            value={weekEndDate}
                            onChange={(d) => {
                                const nd = normalizeDate(d);
                                if (nd) setWeekEndDate(nd);
                            }}
                            format="DD-MM-YYYY"
                        />
                    </div>
                )}

                {dateFilter === "this_month" && (
                    <DatePicker
                        value={monthDate}
                        onChange={(d) => {
                            const nd = normalizeDate(d);
                            if (nd) setMonthDate(nd);
                        }}
                        format="MMM-YYYY"
                        onlyMonthPicker
                    />
                )}

                {dateFilter === "custom" && (
                    <div className='d-flex gap-2'>
                        <DatePicker
                            value={customStartDate}
                            onChange={(d) => {
                                const nd = normalizeDate(d);
                                if (nd) setCustomStartDate(nd);
                            }}
                            format="DD-MM-YYYY"
                        />
                        <DatePicker
                            value={customEndDate}
                            onChange={(d) => {
                                const nd = normalizeDate(d);
                                if (nd) setCustomEndDate(nd);
                            }}
                            format="DD-MM-YYYY"
                        />
                    </div>
                )}

                <div className="d-flex gap-2 mt-3">
                    <Button variant="secondary" onClick={handleResetFilter}>Reset</Button>
                    <Button variant="primary" onClick={handleApplyFilter}>Apply</Button>
                </div>
            </Drawer>  

             
        </>
    );
};

export default Manage_FoodWastage;
